/**
 * @file 学びメモを1件開いて書く間の状態と保存を持つ hook である。
 *
 * @what: 開いているメモの下書き (題・本文・紐づけ・画像・タグ)、自動保存、画像の追加と表示、
 *   削除、その日に書き足したぶんの記録 (「さいきん書いたメモ」)、閉じるときの保存を持つ。
 *   本を読む画面のメモ (StudyNotePane) と学びメモの画面 (StudyNotesRoute) が使い、見た目は
 *   components/StudyNoteEditor.tsx が持つ。
 * @why: 2つの画面で保存の決まり (本文は欄の側を正にする・打鍵が止まってから送る・閉じるときに
 *   待たずに送る) を分けて持つと、片方だけ直して食い違う。どの文脈のメモを開くか (ページに合う
 *   メモを自動で開く・タグで絞る) は画面ごとに違うので、画面の側に残す。
 * @failure: 保存の失敗は書きかけを消さず、文言を出して次の変更でもう一度試す。新しいメモが件数の
 *   上限で断られ、まだ何も書いていなければ、そのメモを閉じて onDiscarded で一覧から外させる。
 *   画像の送信失敗は本文を変えない。削除の失敗は文言だけ出す (詳細は console)。
 *
 * 本文の欄だけは React に値を持たせない (hooks/useUncontrolledText.ts に理由がある)。
 * state への反映は打鍵が止まってから送り、保存に送る本文はいつも欄の側から取る。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/shared/api/client";
import {
  deleteStudyNote,
  putStudyNote,
  type StudyNote,
  type StudyNoteImage,
  type StudyNoteRef,
} from "../api/notes";
import { postNoteEntry } from "../api/noteEntries";
import { todayLocal } from "../api/dates";
import { acquirePageImage, releasePageImage } from "../lib/pageImageCache";
import { deleteStudyNoteImage, uploadStudyNoteImage } from "../lib/studyLogImage";
import { noteEntryDiff, type NoteEntrySnapshot } from "../model/noteEntryDiff";
import {
  loadNoteEntrySession,
  newNoteEntrySegmentId,
  saveNoteEntrySession,
  type NoteEntrySession,
} from "../model/noteEntrySession";
import { noteImageSnippet } from "../model/noteImages";
import { useUncontrolledText } from "./useUncontrolledText";

/** 自動保存までの待ち (ms)。打鍵のたびに延びる。 */
const AUTOSAVE_DELAY_MS = 2500;

/**
 * 本文を state へ送るまでの待ち (ms)。打鍵のたびに延びる。
 *
 * 打鍵そのものは state を通さないので、ここで遅れるのはプレビュー・聞く対象の見出し・
 * 自動保存の起点だけである。保存に送る本文はいつも bodyTextRef から取るので遅れない。
 */
const BODY_SYNC_DELAY_MS = 600;

/** 1つのノートに付けられる画像の上限 (サーバの StudyNoteMaxImages と同じ値)。 */
export const NOTE_IMAGES_MAX = 30;

/** 新しいメモが件数の上限で断られたときの文言 (代表の判断、2026-10-29)。 */
export const NOTE_LIMIT_MESSAGE = "これ以上メモを増やせません。追加開発を検討してください。";

export interface NoteDraft {
  /** まなぶ人である。学習者を持たない古いメモは空で、保存のときにいまのまなぶ人が入る。 */
  learnerPersonId: string;
  title: string;
  body: string;
  refs: StudyNoteRef[];
  images: StudyNoteImage[];
  tags: string[];
}

export function draftOf(note: StudyNote): NoteDraft {
  return {
    learnerPersonId: note.learner_person_id ?? "",
    title: note.title,
    body: note.body ?? "",
    refs: note.refs ?? [],
    images: note.images ?? [],
    tags: note.tags ?? [],
  };
}

/** 題が空のまま保存するときの題である。 */
const UNTITLED = "むだいのノート";

function isNoteLimitError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body as { error_code?: unknown } | undefined;
  return body?.error_code === "study_note_limit_reached";
}

export interface StudyNoteEditorOptions {
  familyId: string;
  /** 画面で選んでいるまなぶ人である。学習者を持たない古いメモの保存に入れる。 */
  learnerPersonId: string | null;
  /** 保存できたメモを、画面の一覧へ入れる。 */
  onSaved: (saved: StudyNote) => void;
  /** 削除したメモを、画面の一覧から外す。 */
  onRemoved: (noteId: string) => void;
  /** 件数の上限で作れなかった新しいメモを、画面の一覧から外す。 */
  onDiscarded: (noteId: string) => void;
  /** 本文の出し方を端末に覚え、画面を開き直しても続ける (学びメモの画面だけ)。 */
  rememberBodyView?: boolean;
}

/** 本文の出し方 (編集欄だけ / 編集欄とプレビューを左右に並べる / プレビューだけ)。 */
export type NoteBodyView = "edit" | "split" | "preview";

const BODY_VIEW_KEY = "bb.study.notebook.bodyView";

/** 覚えている本文の出し方 (覚えていなければ編集欄だけ)。 */
function readBodyView(): NoteBodyView {
  try {
    const v = window.localStorage.getItem(BODY_VIEW_KEY);
    return v === "split" || v === "preview" ? v : "edit";
  } catch {
    return "edit";
  }
}

/** 本文の出し方を覚える。 */
function writeBodyView(view: NoteBodyView): void {
  try {
    if (view === "edit") window.localStorage.removeItem(BODY_VIEW_KEY);
    else window.localStorage.setItem(BODY_VIEW_KEY, view);
  } catch {
    // 覚えられなくても、その画面のあいだは切り替えが効く
  }
}

export function useStudyNoteEditor({
  familyId,
  learnerPersonId,
  onSaved,
  onRemoved,
  onDiscarded,
  rememberBodyView = false,
}: StudyNoteEditorOptions) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NoteDraft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // 新しいメモが件数の上限で断られた (画面の上に出す)。メモを開き直すか作り直すまで残す。
  const [limitReached, setLimitReached] = useState(false);
  // 本文の出し方。メモを替えても続ける (代表の指示、2026-11-02)。新しいメモだけはプレビューで
  // 出すものが無いので、プレビューなら編集にして開く。
  const [bodyView, setBodyView] = useState<NoteBodyView>(() =>
    rememberBodyView ? readBodyView() : "edit",
  );
  useEffect(() => {
    if (rememberBodyView) writeBodyView(bodyView);
  }, [rememberBodyView, bodyView]);
  // 削除の確認 (ダイアログ)。誤操作で長文の資産を失わないため、必ず1段はさむ。
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // 削除の実行中 (ダイアログのボタンを無効にし、二重送信を防ぐ)。
  const [removing, setRemoving] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 本文の欄は React に値を持たせない (hooks/useUncontrolledText.ts に理由がある)。
  const { textRef: bodyTextRef, attach: attachBodyText, put: putBody } = useUncontrolledText();
  const bodySyncTimer = useRef<number | null>(null);
  // まだサーバに1度も保存できていない新しいメモ (件数の上限で断られたら一覧から外す)。
  const unsavedIds = useRef(new Set<string>());

  /** カーソル位置を読むため、欄そのものも持っておく。 */
  const attachBody = useCallback(
    (node: HTMLTextAreaElement | null) => {
      bodyRef.current = node;
      attachBodyText(node);
    },
    [attachBodyText],
  );

  // 開いているノートの画像 (id → blob URL)。取得した path は切り替え・unmount で返す。
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const acquiredPaths = useRef<string[]>([]);

  const acquireImage = useCallback(async (img: StudyNoteImage) => {
    try {
      const url = await acquirePageImage(
        img.path,
        img.path.endsWith(".png") ? "image/png" : "image/jpeg",
      );
      acquiredPaths.current.push(img.path);
      setImageUrls((prev) => new Map(prev).set(img.id, url));
    } catch (err) {
      // 読めない画像は参照がそのまま見えるだけに留める (本文は壊さない)。
      console.error("[study] failed to load a note image", err);
    }
  }, []);

  const releaseAllImages = useCallback(() => {
    for (const p of acquiredPaths.current) releasePageImage(p);
    acquiredPaths.current = [];
    setImageUrls(new Map());
  }, []);

  useEffect(() => releaseAllImages, [releaseAllImages]);

  /**
   * メモごとの、書き足したぶんを測る起点である。その日に初めて開いた時点の本文と画像を持ち、保存の
   * たびにそれとの差を「さいきん書いたメモ」の記録へ送る (model/noteEntryDiff.ts)。同じ開き方の送信は
   * サーバが置き換えるので、自動保存が打鍵の途中で走っても書きかけの行は段階ごとに残らない。
   * 置き場は model/noteEntrySession.ts で、本だなへ戻って開き直しても同じ日なら同じ起点と id を使う。
   * 日付が変わったら、直前に保存した姿を新しい起点にして開き方の id も付け直す。
   */
  // 直前に保存した姿である (日付が変わったときの起点にする)
  const lastSavedRef = useRef(new Map<string, NoteEntrySnapshot>());
  const entryContextFor = useCallback(
    (noteId: string, current: NoteEntrySnapshot): NoteEntrySession => {
      const today = todayLocal();
      const stored = loadNoteEntrySession(familyId, noteId);
      if (stored !== null && stored.date === today) return stored;
      const fresh: NoteEntrySession = {
        date: today,
        segmentId: newNoteEntrySegmentId(),
        base: lastSavedRef.current.get(noteId) ?? current,
        sent: false,
      };
      saveNoteEntrySession(familyId, noteId, fresh);
      return fresh;
    },
    [familyId],
  );

  const openNote = useCallback(
    (note: StudyNote) => {
      putBody(note.body ?? "");
      // 開いた時点の姿を、書き足したぶんを測る起点にする (同じ日に開き直しても起点は変えない)
      const snapshot = { body: note.body ?? "", imageIds: (note.images ?? []).map((i) => i.id) };
      if (!lastSavedRef.current.has(note.id)) lastSavedRef.current.set(note.id, snapshot);
      entryContextFor(note.id, snapshot);
      setSelectedId(note.id);
      setDraft(draftOf(note));
      setDirty(false);
      setSaveError(null);
      setLimitReached(false);
      setSavedAt(null);
      setConfirmingDelete(false);
      releaseAllImages();
      for (const img of note.images ?? []) void acquireImage(img);
    },
    [releaseAllImages, acquireImage, putBody, entryContextFor],
  );

  /** 開いているメモを閉じる (書きかけは捨てる。保存してから閉じるのは呼ぶ側の役目)。 */
  const closeNote = useCallback(() => {
    setSelectedId(null);
    setDraft(null);
    setDirty(false);
    releaseAllImages();
  }, [releaseAllImages]);

  const recordEntry = useCallback(
    (noteId: string, learner: string, next: NoteDraft, title: string) => {
      const snapshot: NoteEntrySnapshot = {
        body: next.body,
        imageIds: next.images.map((i) => i.id),
      };
      const ctx = entryContextFor(noteId, snapshot);
      lastSavedRef.current.set(noteId, snapshot);
      const diff = noteEntryDiff(ctx.base, snapshot) ?? { text: "", images: 0 };
      // 何も足していなければ送らない。一度送ったあとに全部消したときだけ、空にする送信をする
      if (diff.text === "" && diff.images === 0 && !ctx.sent) return;
      if (learner === "") return;
      const sent = diff.text !== "" || diff.images > 0;
      if (sent !== ctx.sent) saveNoteEntrySession(familyId, noteId, { ...ctx, sent });
      const sourceIds = [
        ...new Set(next.refs.filter((r) => r.kind === "source").map((r) => r.source_id)),
      ];
      // 送れなくても保存は済んでいるので、利用者には知らせない (次の保存でもう一度送る)
      postNoteEntry(familyId, learner, noteId, {
        date: ctx.date,
        title,
        source_ids: sourceIds,
        segment_id: ctx.segmentId,
        text: diff.text,
        images: diff.images,
      }).catch((err: unknown) => {
        console.error("[study] failed to record a note entry", err);
      });
    },
    [familyId, entryContextFor],
  );

  const save = useCallback(
    async (noteId: string, next: NoteDraft) => {
      setSaving(true);
      setSaveError(null);
      try {
        // 学習者を持たない古いメモは、いま開いているまなぶ人のものにする
        const learner = next.learnerPersonId || (learnerPersonId ?? "");
        const title = next.title.trim() === "" ? UNTITLED : next.title;
        const saved = await putStudyNote(familyId, noteId, {
          learner_person_id: learner,
          title,
          body: next.body,
          refs: next.refs,
          images: next.images,
          tags: next.tags,
        });
        unsavedIds.current.delete(noteId);
        onSaved(saved);
        setDirty(false);
        recordEntry(noteId, learner, next, title);
        setSavedAt(new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" }));
        return true;
      } catch (err) {
        if (isNoteLimitError(err)) {
          setLimitReached(true);
          // まだ何も書いていない新しいメモは一覧から外す。書いた本文があれば、消さずに開いたままにする。
          if (unsavedIds.current.has(noteId) && bodyTextRef.current.trim() === "") {
            unsavedIds.current.delete(noteId);
            onDiscarded(noteId);
            closeNote();
          } else {
            setSaveError(NOTE_LIMIT_MESSAGE);
          }
          return false;
        }
        console.error("[study] failed to save a note", err);
        setSaveError("保存できませんでした。つづきを書くともう一度試します。");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [familyId, learnerPersonId, onSaved, onDiscarded, recordEntry, closeNote, bodyTextRef],
  );

  /**
   * 保存に送る下書きである。本文だけは state ではなく欄の側を正にする。
   * ※なぜなら本文の state への反映は打鍵が止まってから送るので、送る時点では state が
   *   最後の数文字ぶん古いことがあるためである。
   */
  const draftToSave = useCallback(
    (base: NoteDraft): NoteDraft => ({ ...base, body: bodyTextRef.current }),
    [bodyTextRef],
  );

  /** 書きかけがあれば保存する。保存できたか、書きかけが無ければ true を返す。 */
  const saveIfDirty = useCallback(async (): Promise<boolean> => {
    if (!dirty || selectedId === null || draft === null) return true;
    return save(selectedId, draftToSave(draft));
  }, [dirty, selectedId, draft, save, draftToSave]);

  // 自動保存 (打鍵が止まってから少し待つ)。
  useEffect(() => {
    if (!dirty || selectedId === null || draft === null) return;
    const timer = window.setTimeout(
      () => void save(selectedId, draftToSave(draft)),
      AUTOSAVE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [dirty, selectedId, draft, save, draftToSave]);

  const edit = useCallback(
    (patch: Partial<NoteDraft>) => {
      // 本文を差し替える変更 (画像の差し込みなど) は、欄と手元の本文にも入れる。
      if (patch.body !== undefined) putBody(patch.body);
      setDraft((prev) => (prev === null ? prev : { ...prev, ...patch }));
      setDirty(true);
    },
    [putBody],
  );

  /** 打鍵のたびに呼ぶ。state は通さず、止まってからまとめて送る。 */
  const onBodyInput = (next: string) => {
    bodyTextRef.current = next;
    setDirty(true);
    if (bodySyncTimer.current !== null) window.clearTimeout(bodySyncTimer.current);
    bodySyncTimer.current = window.setTimeout(() => {
      bodySyncTimer.current = null;
      setDraft((prev) =>
        prev === null || prev.body === bodyTextRef.current
          ? prev
          : { ...prev, body: bodyTextRef.current },
      );
    }, BODY_SYNC_DELAY_MS);
  };

  /** 待たずに state へ送る (プレビューへ移るときと、欄から離れるとき)。 */
  const flushBody = useCallback(() => {
    if (bodySyncTimer.current !== null) {
      window.clearTimeout(bodySyncTimer.current);
      bodySyncTimer.current = null;
    }
    setDraft((prev) =>
      prev === null || prev.body === bodyTextRef.current
        ? prev
        : { ...prev, body: bodyTextRef.current },
    );
  }, [bodyTextRef]);

  useEffect(() => {
    return () => {
      if (bodySyncTimer.current !== null) window.clearTimeout(bodySyncTimer.current);
    };
  }, []);

  /**
   * 新しいメモを開き、骨組みを先に保存する (書き始める前に閉じても一覧に残る)。
   * 件数の上限で断られたら、そのメモは一覧から外れる (save の @failure)。
   */
  const createNote = useCallback(
    async (note: StudyNote) => {
      unsavedIds.current.add(note.id);
      onSaved(note);
      openNote(note);
      setBodyView((v) => (v === "preview" ? "edit" : v));
      await save(note.id, draftOf(note));
    },
    [onSaved, openNote, save],
  );

  const removeNote = async () => {
    if (selectedId === null || removing) return false;
    setRemoving(true);
    setSaveError(null);
    try {
      await deleteStudyNote(familyId, selectedId);
      // 付けた画像も片づける (best-effort。未保存の attach 分も draft が持っている)。
      for (const img of draft?.images ?? []) deleteStudyNoteImage(img.path);
      onRemoved(selectedId);
      closeNote();
      setConfirmingDelete(false);
      return true;
    } catch (err) {
      console.error("[study] failed to delete a note", err);
      setSaveError("消せませんでした。時間を置いてお試しください。");
      setConfirmingDelete(false);
      return false;
    } finally {
      setRemoving(false);
    }
  };

  /** 画像を上げ、カーソル位置に参照を差し込む (貼り付け・ドロップ・ボタンの共通)。 */
  const attachImage = async (file: File) => {
    if (selectedId === null || draft === null || attaching) return;
    if (draft.images.length >= NOTE_IMAGES_MAX) {
      setSaveError(`画像は1つのノートに${NOTE_IMAGES_MAX}枚までです。`);
      return;
    }
    setAttaching(true);
    setSaveError(null);
    try {
      const uploaded = await uploadStudyNoteImage(file, familyId, selectedId);
      const ta = bodyRef.current;
      const current = bodyTextRef.current;
      const pos = ta !== null ? ta.selectionStart : current.length;
      const body = current.slice(0, pos) + noteImageSnippet(uploaded.id) + current.slice(pos);
      edit({ body, images: [...draft.images, uploaded] });
      void acquireImage(uploaded);
    } catch (err) {
      console.error("[study] failed to upload a note image", err);
      setSaveError("画像を追加できませんでした。時間を置いてお試しください。");
    } finally {
      setAttaching(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  /** 手書きで作った画像を、本文の末尾の参照とともに足す。 */
  const appendCreatedImage = useCallback(
    (created: StudyNoteImage) => {
      const body = bodyTextRef.current.trimEnd() + "\n\n" + noteImageSnippet(created.id) + "\n";
      putBody(body);
      setDraft((d) => (d === null ? d : { ...d, body, images: [...d.images, created] }));
      setDirty(true);
      void acquireImage(created);
    },
    [bodyTextRef, putBody, acquireImage],
  );

  // 画面が閉じられる (unmount) ときに書きかけがあれば、待たずに保存する。
  // ※なぜならサイドペインの切り替え (手書き → ページに書き込む) でペインごと消え、2.5秒の
  //   間引きの途中の変更 (手書きの参照など) が失われるためである。
  const latestRef = useRef({ dirty, selectedId, draft });
  latestRef.current = { dirty, selectedId, draft };
  useEffect(() => {
    return () => {
      const { dirty: d, selectedId: id, draft: dr } = latestRef.current;
      if (!d || id === null || dr === null) return;
      void putStudyNote(familyId, id, {
        learner_person_id: dr.learnerPersonId || (learnerPersonId ?? ""),
        title: dr.title.trim() === "" ? UNTITLED : dr.title,
        body: bodyTextRef.current,
        refs: dr.refs,
        images: dr.images,
        tags: dr.tags,
      }).catch((err: unknown) => {
        console.error("[study] failed to save a note on leave", err);
      });
    };
    // familyId / learnerPersonId は unmount の時点の値で足りる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    selectedId,
    draft,
    dirty,
    saving,
    savedAt,
    saveError,
    setSaveError,
    limitReached,
    bodyView,
    setBodyView,
    confirmingDelete,
    setConfirmingDelete,
    removing,
    attaching,
    imageUrls,
    acquireImage,
    releaseAllImages,
    bodyTextRef,
    attachBody,
    fileInputRef,
    openNote,
    closeNote,
    createNote,
    edit,
    onBodyInput,
    flushBody,
    save,
    draftToSave,
    saveIfDirty,
    removeNote,
    attachImage,
    appendCreatedImage,
  };
}

export type StudyNoteEditorState = ReturnType<typeof useStudyNoteEditor>;
